import { DialogComponent } from '@theme/dialog';
import { Fleet, selfHeal, shopUrl } from '@theme/fleet';

/**
 * <fleet-menu> — the header chip + slide-out drawer for My Fleet.
 *
 * Mirrors the cart drawer (snippets/cart-drawer.liquid + assets/dialog.js): the
 * chip button opens a native <dialog> styled as a right-side drawer. The drawer
 * lists saved machines (radio-style), lets the shopper switch/remove/add, and
 * deep-links the active machine to /pages/fitment-search.
 *
 * Rows are built in JS (Liquid can't read localStorage) and re-rendered whenever
 * the Fleet store dispatches `fleet:change`. Row buttons use the theme's
 * declarative `on:click` delegation; they carry the row index to stay robust
 * against special characters in machine ids.
 */

const ICON_REMOVE =
  '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></svg>';

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

class FleetMenu extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.chipLabel = this.querySelector('[data-ref="chipLabel"]');
    this.chipCount = this.querySelector('[data-ref="chipCount"]');
    this.chipBtn = this.querySelector('.fleet-menu__chip');
    this.listEl = this.querySelector('[data-ref="list"]');
    this.emptyEl = this.querySelector('[data-ref="empty"]');
    this.shopBtn = this.querySelector('[data-ref="shop"]');
    this.addPanel = this.querySelector('[data-ref="addPanel"]');
    this.emptyLabel = this.dataset.emptyLabel || 'My Fleet';

    this.render = this.render.bind(this);
    // Render now + on every fleet:change, and re-render if Horizon's post-paint
    // section morph reverts the chip/list to server HTML (self-heal — fixes the
    // chip blanking on refresh while the machines are still saved).
    this._heal = selfHeal(this, this.render);
    // Collapse the add panel once a machine is added. Stable handler reference so
    // it can be removed on disconnect (avoids leaking a listener per reconnect).
    this.onAdded = () => {
      if (this.addPanel) this.addPanel.hidden = true;
    };
    this.addEventListener('fleet:added', this.onAdded);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._heal) this._heal.stop();
    this.removeEventListener('fleet:added', this.onAdded);
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  toggleCascade() {
    if (this.addPanel) this.addPanel.hidden = !this.addPanel.hidden;
  }

  select(data) {
    const m = this._rows && this._rows[data.i];
    if (m) Fleet.setActive(m.id);
  }

  remove(data) {
    const m = this._rows && this._rows[data.i];
    if (m) Fleet.remove(m.id);
  }

  shop() {
    const active = Fleet.getActive();
    if (active) window.location.href = shopUrl(active);
  }

  render() {
    const machines = Fleet.list();
    const activeId = Fleet.getActiveId();
    this._rows = machines;
    const active = machines.find((m) => m.id === activeId) || null;

    if (this.chipLabel) this.chipLabel.textContent = active ? active.label : this.emptyLabel;
    this.toggleAttribute('data-has-active', Boolean(active));

    const count = machines.length;
    if (this.chipCount) {
      this.chipCount.textContent = String(count);
      this.chipCount.hidden = count === 0;
    }
    if (this.chipBtn) {
      this.chipBtn.setAttribute(
        'aria-label',
        count === 0
          ? this.emptyLabel
          : `${this.emptyLabel}, ${count} ${count === 1 ? 'machine' : 'machines'} saved`
      );
    }

    if (this.listEl) {
      this.listEl.innerHTML = machines
        .map((m, i) => {
          const checked = m.id === activeId;
          return `
            <li class="fleet-list__row" role="radio" aria-checked="${checked}">
              <button type="button" class="fleet-list__select" on:click="fleet-menu/select?i=${i}">
                <span class="fleet-list__radio" aria-hidden="true"></span>
                <span class="fleet-list__label">${esc(m.label)}</span>
                ${checked ? '<span class="fleet-list__badge">Active</span>' : ''}
              </button>
              <button type="button" class="fleet-list__remove" on:click="fleet-menu/remove?i=${i}" aria-label="Remove ${esc(m.label)}">${ICON_REMOVE}</button>
            </li>`;
        })
        .join('');
    }

    if (this.emptyEl) this.emptyEl.hidden = machines.length > 0;
    if (this.shopBtn) this.shopBtn.disabled = !active;
  }
}

if (!customElements.get('fleet-menu')) customElements.define('fleet-menu', FleetMenu);
